/**
 * #1391 — set / clear a project's deployed URL (`liveUrl`).
 *
 * When set, the analyzer's live-page probe fetches route candidates
 * against this origin to extract page-level names. Explicitly clearing
 * (button "Clear") sends `null` so any previously-set value is removed.
 *
 * Consistent styling with `ExclusionSettings.tsx` — same modal shell.
 */
import { useState, useEffect } from 'react';
import { api } from '../../services/api';

interface LiveUrlSettingsProps {
  projectId: string;
  onClose: () => void;
}

function isValidHttpUrl(raw: string): boolean {
  if (!raw) return false;
  try {
    const u = new URL(raw);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export function LiveUrlSettings({ projectId, onClose }: LiveUrlSettingsProps) {
  const [value, setValue] = useState<string>('');
  const [initial, setInitial] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  useEffect(() => {
    api.getProject(projectId).then(project => {
      const cur = project.liveUrl ?? null;
      setInitial(cur);
      setValue(cur ?? '');
      setIsLoading(false);
    }).catch(() => setIsLoading(false));
  }, [projectId]);

  async function handleSave() {
    setIsSaving(true);
    setMessage(null);
    try {
      const trimmed = value.trim();
      if (trimmed && !isValidHttpUrl(trimmed)) {
        setMessage({ type: 'error', text: 'Please enter a valid http(s) URL.' });
        setIsSaving(false);
        return;
      }
      await api.updateProject(projectId, {
        // Send explicit null when cleared so the server-side unset happens.
        liveUrl: trimmed ? trimmed : null,
      });
      setInitial(trimmed || null);
      setMessage({
        type: 'success',
        text: trimmed ? `Live URL saved: ${trimmed}` : 'Live URL cleared.',
      });
    } catch {
      setMessage({ type: 'error', text: 'Failed to save. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  }

  const dirty = value.trim() !== (initial ?? '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-lg mx-4 flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold">Live URL</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Deployed origin the analyzer probes for page titles + metadata (#1391).
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="live-url">
              Deployed URL
            </label>
            {isLoading ? (
              <div className="text-sm text-muted-foreground py-1.5">Loading…</div>
            ) : (
              <input
                id="live-url"
                type="url"
                placeholder="https://stage.example.com"
                value={value}
                onChange={e => setValue(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-transparent"
                autoFocus
              />
            )}
            <p className="text-xs text-muted-foreground leading-relaxed">
              Leave empty to disable. Probes only touch this origin — never external hosts.
              Rate-limited by default (100&nbsp;ms between requests, capped at 40 pages/run).
            </p>
          </div>

          {/* Hint about what happens with this set */}
          <div className="bg-muted/50 rounded-lg px-4 py-3 text-sm">
            <p className="font-medium text-foreground mb-1.5">What we extract</p>
            <ul className="text-muted-foreground text-xs leading-relaxed list-disc list-inside space-y-0.5">
              <li>&lt;title&gt; — canonical page name</li>
              <li>og:title / og:description — hand-authored social metadata</li>
              <li>First &lt;h1&gt; — what a user reads on-screen</li>
              <li>Brand suffix stripped from titles like &quot;Brand&nbsp;-&nbsp;Page&quot;</li>
            </ul>
          </div>

          {message && (
            <div
              className={`text-sm px-3 py-2 rounded-md border ${
                message.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                  : message.type === 'error'
                    ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
                    : 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300'
              }`}
            >
              {message.text}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-border">
          <button
            onClick={() => setValue('')}
            className="text-sm px-3 py-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
            disabled={!value || isSaving}
          >
            Clear
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="text-sm px-3 py-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="text-sm px-4 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              disabled={isSaving || (!dirty && !message)}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
